/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable consistent-return */
/* eslint-disable no-await-in-loop */
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useRouter } from 'next/router';
import { showOnDesktopOnly } from 'styles/utils/responsiveness';
import styled from 'styled-components';
import { toggleVisible } from 'styles/theme/animation';
import { ScrollToTopArrow } from 'shared/components/ScrollToTopArrow';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { debounce } from 'lodash';
import { Loader } from '../../Loader';

const PAGE_SIZE = 12;

const VirtualizedGrid = ({
  ItemRenderer,
  fetchItems,
  totalNumberOfItems,
  initialPages,
  initialPageParams,
  columnCount,
  height,
  width,
  getGridHeader,
  rowsPerPage,
  offset,
  toggleHeader,
  onScrollGrid,
  hideMetadata,
  queryKeys,
  timelineData = {},
}) => {
  const { currentTheme } = useStoreSelector((state) => state.pageLayout);
  const router = useRouter();
  const queryClient = useQueryClient();
  const parentRef = useRef<any>(null);
  const lastScrollTopRef = useRef<any>();
  // const { ref: bottomRef, inView: bottomInView } = useInView({
  //   threshold: 0.01,
  // });

  const [containerWidth, setContainerWidth] = useState(width); // initial fallback
  const aspectRatio =
    width < 500 ? 130 / 290 : width < 830 ? 376 / 395 : width < 1180 ? 286 / 290 : 400 / 395;
  const rowHeight = Math.max(
    (containerWidth / columnCount) * aspectRatio,
    width < 500 ? 290 : width < 830 ? 395 : width < 1180 ? 290 : 395,
  );

  const [initialPage, setInitialPage] = useState(
    Number.parseInt((router.query.pageNumber as string) || '1', 10),
  );

  const [showScrollToTopArrow, setShowScrollToTopArrow] = useState(
    Number.parseInt((router.query.pageNumber as string) || '1', 10) > 1,
  );
  const [hasScrolledToInitial, setHasScrolledToInitial] = useState(false);
  const [blockingPage, setBlockingPage] = useState(null);
  const [queryEnabled, setQueryEnabled] = useState(false);
  const hasLoadedPlaceholder = useRef<Set<number | undefined>>(new Set());

  const [hasScrollSettled, setHasScrollSettled] = useState(false); // 👈 scroll "done"
  const scrollTimeout = useRef(null);

  // const fetchData = useCallback([totalNumberOfItems, fetchItems]);

  useEffect(() => {
    if (hasScrollSettled && hasScrolledToInitial) {
      // ✅ Scrolling has finished

      setQueryEnabled(true); // for example
    }
  }, [hasScrollSettled, hasScrolledToInitial]);

  useEffect(() => {
    if (router.isReady) {
      const page = Number.parseInt((router.query.pageNumber as string) || '1', 10);
      setInitialPage(page);
    }
  }, [router.isReady, router.query.pageNumber]);

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    queryKey: ['items', ...queryKeys],
    queryFn: async (props) => {
      const start = (props.pageParam - 1) * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const res = await fetchItems({ page: props.pageParam, pageSize: 12 });

      const items = res?.data || [];

      return {
        items,
        prevPage: props.pageParam > 1 ? props.pageParam - 1 : undefined,
        nextPage: end < totalNumberOfItems ? props.pageParam + 1 : undefined,
        hasMore: end < totalNumberOfItems,
        page: props.pageParam,
      };
    },
    enabled: false,
    initialPageParam: initialPage,
    getPreviousPageParam: (firstPage) => firstPage.prevPage,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextPage : undefined),
    initialData: () => {
      return {
        pages: initialPages,
        pageParams: initialPageParams,
      };
    },
  });

  // const items = data?.pages.flatMap((page) => page.items || []) || [];
  const items = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data]);

  const initialOffset = useMemo(() => {
    if (!initialPage || columnCount <= 0) return 0;
    const rowIndex = Math.floor(((initialPage - 1) * PAGE_SIZE) / columnCount);
    return rowIndex * rowHeight;
  }, [initialPage, columnCount, rowHeight]);

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(items.length / columnCount),
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    initialOffset,
  });

  useEffect(() => {
    if (!parentRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      rowVirtualizer.measure(); // 👈 re-measure all rows
    });
    observer.observe(parentRef.current);
    return () => observer.disconnect();
  }, [columnCount, rowVirtualizer]);

  useEffect(() => {
    const scrollEl = parentRef.current;
    if (!scrollEl) return;

    if (initialPage === 1) {
      setHasScrollSettled(true);
      return;
    }

    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      setShowScrollToTopArrow(scrollEl.scrollTop > 0);
      scrollTimeout.current = setTimeout(() => {
        setHasScrollSettled(true);
      }, 100); // ~100ms after last scroll event
    };

    scrollEl.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [parentRef.current, initialPage]);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Load more pages when intersection triggers

  // useEffect(() => {
  //   if (bottomInView && hasNextPage && !isFetching) {
  //     fetchNextPage();
  //   }
  // }, [bottomInView, hasNextPage, isFetching]);

  useEffect(() => {
    if (!queryEnabled || !bottomRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      },
      {
        root: null,
        threshold: 0.01,
        rootMargin: '100px 0px 100px 0px',
      },
    );

    observer.observe(bottomRef.current);

    return () => observer.disconnect();
  }, [queryEnabled, hasNextPage, isFetching, fetchNextPage]);

  // Scroll to initial page only once when data is ready

  const isBackNavigation = useRef(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const onPopState = () => {
        isBackNavigation.current = true;
      };
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
    }
  }, []);

  useEffect(() => {
    // if (initialPage === 1) {
    //   setHasScrolledToInitial(true);
    // } else if (!hasScrolledToInitial && initialPage !== null && items.length > 0) {
    //   if (!rowVirtualizer || !initialPage || columnCount <= 0) return;

    //   const scrollToIndex = Math.floor(((initialPage - 1) * PAGE_SIZE) / columnCount);

    //   // Wait until layout is committed
    //   if (!isBackNavigation.current) {
    //     console.log('scroll scroll', parentRef.current);
    //     requestAnimationFrame(() => {
    //       rowVirtualizer.scrollToIndex(scrollToIndex, { align: 'start' });
    //     });

    //     if (isMobileDevice()) {
    //       setTimeout(() => {
    //         if (parentRef.current) {
    //           parentRef.current.style.overflowY = 'hidden';
    //           void parentRef.current.offsetHeight;
    //           parentRef.current.style.overflowY = 'auto';
    //         }
    //       }, 300);
    //     }
    //     requestAnimationFrame(() => {
    //       setHasScrolledToInitial(true);
    //       setHasScrollSettled(true);
    //       setTimeout(() => {
    //         rowVirtualizer.measure(); // force remeasure after scroll
    //       }, 100);
    //     });
    //   } else {
    //     isBackNavigation.current = false; // ✅ Reset it

    //     requestAnimationFrame(() => {
    setHasScrolledToInitial(true);
    setHasScrollSettled(true);
    //       setTimeout(() => {
    //         rowVirtualizer.measure(); // force remeasure after scroll
    //       }, 100);
    //     });
    //   }
    // }
  }, []);

  // here is to setup the current page

  // here is to scroll up and replace fake data
  const didMountRef = useRef<any>(false);

  useEffect(() => {
    if (!queryEnabled || !parentRef.current) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const el = entry.target as HTMLElement;
          const page = Number(el.dataset.page);
          const isPlaceholder = el.dataset.placeholder === 'true';

          if (
            !isPlaceholder ||
            Number.isNaN(page) ||
            blockingPage ||
            hasLoadedPlaceholder.current.has(page)
          )
            continue;

          setBlockingPage(page);

          try {
            const newPage = await fetchItems({ page, pageSize: 12 });
            hasLoadedPlaceholder.current.add(page);

            queryClient.setQueryData(['items', ...queryKeys], (oldData: any) => {
              if (!oldData) return oldData;
              const updatedPages = oldData.pages.map((p: any) =>
                p.page === page ? { ...p, items: newPage.data } : p,
              );
              return { ...oldData, pages: updatedPages };
            });
          } finally {
            setBlockingPage(null);
          }
        }
      },
      {
        threshold: 0.01, // lower threshold for Safari compatibility
        root: null, // use viewport
      },
    );

    const observePlaceholders = () => {
      requestAnimationFrame(() => {
        const placeholders = parentRef.current?.querySelectorAll('[data-placeholder="true"]') ?? [];
        placeholders.forEach((el) => {
          try {
            observer.observe(el);
          } catch {
            // Safari sometimes throws on detached nodes
          }
        });
      });
    };

    observePlaceholders();

    const mutationObserver: any = new MutationObserver(() => {
      // Debounced to avoid Safari mutation storm
      clearTimeout(mutationObserver._timeout);
      mutationObserver._timeout = setTimeout(observePlaceholders, 50);
    });

    mutationObserver.observe(parentRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      clearTimeout(mutationObserver._timeout);
    };
  }, [queryEnabled, blockingPage, queryKeys, fetchItems]);

  useEffect(() => {
    // currentInitialPages.current = initialPages;

    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    if (!initialPages) return;

    // Clear internal states
    setInitialPage(1);
    hasLoadedPlaceholder.current = new Set();

    // Clear React Query cache
    queryClient.setQueryData(['items', ...queryKeys], {
      pages: initialPages,
      pageParams: initialPageParams,
    });

    // Reset scroll position
    if (router.query.pageNumber === '1') {
      rowVirtualizer.scrollToOffset(0);
    }
    setShowScrollToTopArrow(false);
  }, [initialPages, initialPageParams]);

  const debouncedScrollUI = useRef<any>(
    debounce(
      (
        scrollTop: number,
        toggleHeaderFn: any,
        setArrowFn: any,
        onScrollGridFn: any,
        isScrollingUp,
      ) => {
        toggleHeaderFn(isScrollingUp);
        setArrowFn(scrollTop > 0);
        onScrollGridFn?.(scrollTop);
      },
      50,
    ),
  ).current;

  // debounce scroll here

  if (initialPage === null) return null;

  return (
    <div
      ref={parentRef}
      className="tw-overflow-y-auto tw-overflow-x-hidden tw-h-screen tw-w-full tw-relative no-scrollbar"
      style={{
        WebkitOverflowScrolling: 'touch', // enable smooth scroll on iOS
        touchAction: 'auto', // prevent pointer lock issues
        overscrollBehavior: 'auto', // let scroll chaining happen
        visibility: queryEnabled ? 'visible' : 'hidden',
        width: width - offset.left,
        marginTop: -1,
      }}
      onScroll={(e: any) => {
        // UI effects
        const { scrollTop } = e.target;
        let isScrollingUp = false;
        if (lastScrollTopRef.current === undefined) {
          lastScrollTopRef.current = scrollTop; // Initialize on first scroll
        } else {
          const lastScrollTop = lastScrollTopRef.current;
          isScrollingUp = scrollTop < lastScrollTop;

          lastScrollTopRef.current = scrollTop; // Update for next scroll
        }

        debouncedScrollUI(
          scrollTop,
          toggleHeader,
          setShowScrollToTopArrow,
          onScrollGrid,
          isScrollingUp,
        );

        // scrollTop comes from here now
        if (queryEnabled) {
          const virtualRows = rowVirtualizer.getVirtualItems();
          const firstVisibleRow = virtualRows.find((v) => v.start >= scrollTop);
          if (!firstVisibleRow) return;

          const firstIndex = firstVisibleRow.index;
          const firstItemIndex = firstIndex * columnCount;
          const currentPage = Math.floor(firstItemIndex / PAGE_SIZE) + 1;

          const urlPage = Number.parseInt((router.query.pageNumber as string) || '1', 10);

          // Prevent infinite replace loop safari
          if (urlPage !== currentPage) {
            void router.replace(
              {
                pathname: router.pathname,
                query: { ...router.query, pageNumber: currentPage },
              },
              undefined,
              { shallow: true },
            );
          }
        }
      }}
    >
      {!hideMetadata && <div>{getGridHeader?.({ isGridHeader: true })}</div>}
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: 'relative',
          width: width - offset.left * 1.5,
        }}
        className="no-scrollbar"
        ref={didMountRef}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow, columnIndex) => {
          const startIdx = virtualRow.index * columnCount;
          const rowItems = items.slice(startIdx, startIdx + columnCount);
          const isFirstRow = virtualRow.index === 0;

          return (
            <div
              key={virtualRow.key}
              className="tw-grid tw-absolute tw-left-0 tw-right-0"
              data-index={virtualRow.index}
              ref={(el) => {
                if (el) rowVirtualizer.measureElement(el);
              }}
              style={{
                transform: `translateY(${virtualRow.start}px)`,
                height: `${rowHeight}px`,
                gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                gap: '0px',
              }}
            >
              {rowItems.map((item, i) => {
                const index = startIdx + i;
                const page = Math.floor(index / PAGE_SIZE) + 1;
                const isPlaceholder = !item?.id;
                const isFirstColumn = i === 0;
                const itemIndex = virtualRow.index * columnCount + columnIndex;
                const itemPosition = (page - 1) * PAGE_SIZE + itemIndex;
                return (
                  <div
                    key={item.id || `${virtualRow.index}-${i}`}
                    data-placeholder={isPlaceholder ? 'true' : undefined}
                    data-page={page}
                    style={{
                      width: '100%',
                      height: '100%',
                      boxSizing: 'border-box',
                      borderRight: `1px solid ${currentTheme?.borderColor || '#000000'}`,
                      borderLeft: isFirstColumn
                        ? `1px solid ${currentTheme?.borderColor || '#000000'}`
                        : 'none',
                      borderBottom: `1px solid ${currentTheme?.borderColor || '#000000'}`,
                      borderTop: isFirstRow
                        ? `1px solid ${currentTheme?.borderColor || '#000000'}`
                        : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      {item?.id ? (
                        <ItemRenderer
                          data={item}
                          position={itemPosition}
                          timelineData={timelineData}
                        />
                      ) : (
                        '  '
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div
        ref={bottomRef}
        style={{
          margin: height / rowsPerPage / 4,
          marginBottom: Math.max(150, height / rowsPerPage / 4),
          marginLeft: columnCount < 3 ? 0 : height / rowsPerPage / 4,
          minHeight: 2,
          width: width - offset.left * 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {isFetching && hasNextPage && (
          <Loader
            style={{
              maxWidth: 400,
              minWidth: 250,
            }}
          />
        )}
      </div>
      <VirtualizedGrid.RightAside>
        <VirtualizedGrid.ScrollToTop $isVisible={showScrollToTopArrow}>
          {!hideMetadata && (
            <ScrollToTopArrow
              onCustomClick={() => {
                void router.push({
                  pathname: router.pathname,
                  query: { ...router.query, pageNumber: '1' },
                });
                rowVirtualizer.scrollToIndex(0, { align: 'start' });
              }}
            />
          )}
        </VirtualizedGrid.ScrollToTop>
      </VirtualizedGrid.RightAside>
    </div>
  );
};

VirtualizedGrid.RightAside = styled.aside`
  ${showOnDesktopOnly`
    display: flex;
    justify-content: flex-end;
    position: fixed;
    bottom: 0;
    right: 0;
  `}
`;

VirtualizedGrid.ScrollToTop = styled.div<{ $isVisible: boolean }>`
  ${showOnDesktopOnly`
    position: sticky;
    align-self: flex-end;
    bottom: 0;
    padding: 38px 14px;
    opacity: 0;
    visibility: hidden;
    ${({ $isVisible }) => $isVisible && toggleVisible};
  `}
`;
export default VirtualizedGrid;
